import React, { useCallback, useEffect } from 'react';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { License } from 'mcsclient/models';
import { useImmer } from 'use-immer';

import { IUserData, pageDataService, unknownUser } from './common/utilities/PageDataService';

type ContextValue = { userData: IUserData; license?: License; refetchLicense: () => Promise<License> | undefined };

const defaultContextValue = { userData: unknownUser, refetchLicense: (): Promise<License> | undefined => undefined };

export const AuthContext = React.createContext<ContextValue>(defaultContextValue);

export interface IAuthProvider {
  children: JSX.Element;
  /**
   * Whether to fetch license on mount
   * @default true
   */
  fetchLicenseOnMount?: boolean;
}

const AuthProvider = ({ children, fetchLicenseOnMount = true }: IAuthProvider) => {
  const [contextValue, setContextValue] = useImmer<ContextValue>({ ...defaultContextValue, userData: pageDataService.getUserData() });

  const refetchLicense = useCallback(() => {
    // Load license info only if it is not webview
    if (!pageDataService.isWebView()) {
      return microsoftConsumerSecurityClient.license.getLicense().then((response) => {
        setContextValue((draft) => {
          draft.license = response;
        });
        return response;
      });
    }
    return undefined;
  }, [setContextValue]);

  useEffect(() => {
    if (fetchLicenseOnMount) {
      refetchLicense();
    }

    setContextValue((draft) => {
      draft.refetchLicense = refetchLicense;
    });
  }, [fetchLicenseOnMount, refetchLicense, setContextValue]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
